import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import useDictatedInput from '../hooks/useDictatedInput';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatEmptyState from './ChatEmptyState';
import PreviewPanel from './PreviewPanel';
import AgentActivity from './AgentActivity';
import SessionDebugPanel from './SessionDebugPanel';
import Toast from './Toast';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { AGENT_STATE, CHAT_EVENTS, FEEDBACK_TOAST_VARIANT, MESSAGE_ROLES, STEP_STATUS, trackChatEvent } from '../constants';
import { getAdminModeEnabled } from '../../selectors/user';
import css from './ChatConversation.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const SCROLL_BOTTOM_THRESHOLD_PX = 50;

const ChatConversation = ({ chat, showEmptyState = false, showSessionDebug = false, onSessionDebugClose = () => {} }) => {
    const {
        sessionType,
        messages,
        input,
        setInput,
        handleSubmit,
        isLoading,
        isCancelling,
        activeRunId,
        error,
        stopError,
        reload,
        stop,
        currentSessionId,
    } = chat;

    const messagesListRef = useRef(null);
    const pendingScrollFrameRef = useRef(0);

    const scrollMessagesToBottom = useCallback(() => {
        const list = messagesListRef.current;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
    }, []);

    const [dashboardArtifacts, setDashboardArtifacts] = useState(null);
    const [artifactsSessionId, setArtifactsSessionId] = useState(currentSessionId);
    if (artifactsSessionId !== currentSessionId) {
        setArtifactsSessionId(currentSessionId);
        setDashboardArtifacts(null);
    }

    const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
    const isPinnedToBottomRef = useRef(true);
    isPinnedToBottomRef.current = isPinnedToBottom;

    const [feedbackToast, setFeedbackToast] = useState(null);

    useEffect(() => {
        if (!feedbackToast) return undefined;
        const timeoutId = setTimeout(() => setFeedbackToast(null), 5000);
        return () => clearTimeout(timeoutId);
    }, [feedbackToast]);

    const handleFeedbackSubmitted = useCallback(() => setFeedbackToast(FEEDBACK_TOAST_VARIANT.SUCCESS), []);
    const handleFeedbackFailed = useCallback(() => setFeedbackToast(FEEDBACK_TOAST_VARIANT.FAILURE), []);

    const isAdminMode = useSelector(getAdminModeEnabled);

    const prevSessionIdRef = useRef(currentSessionId);

    const isInputDisabled = isLoading || isCancelling;

    const hasMessages = messages.length > 0;
    const prevMessagesCountRef = useRef(messages.length);

    const handleScroll = useCallback(() => {
        const list = messagesListRef.current;
        if (!list) return;
        const distanceFromBottom = list.scrollHeight - list.clientHeight - list.scrollTop;
        const atBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
        setIsPinnedToBottom(prev => (prev === atBottom ? prev : atBottom));
    }, []);

    useEffect(() => () => cancelAnimationFrame(pendingScrollFrameRef.current), []);

    const [completedRunCount, setCompletedRunCount] = useState(0);
    const prevIsLoadingRef = useRef(isLoading);
    useEffect(() => {
        if (prevIsLoadingRef.current && !isLoading) setCompletedRunCount(c => c + 1);
        prevIsLoadingRef.current = isLoading;
    }, [isLoading]);

    const {
        isListening: isMicListening,
        isSupported: isMicSupported,
        toggleDictation: toggleMic,
        stopDictation: stopMic,
        onManualChange,
        finishDictation,
        markVoiceUnused,
    } = useDictatedInput({ value: input, onChange: setInput });

    const onMicClick = useCallback(() => {
        if (isMicListening) {
            trackChatEvent(CHAT_EVENTS.MICROPHONE_STOP, {
                sessionId: currentSessionId,
                hasTranscript: !!input.trim(),
            });
        } else {
            trackChatEvent(CHAT_EVENTS.MICROPHONE_START, { sessionId: currentSessionId });
        }
        toggleMic();
    }, [toggleMic, input, isMicListening, currentSessionId]);

    const handleStop = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.STOP_GENERATION, { sessionId: currentSessionId, runId: activeRunId });
        stop();
    }, [stop, currentSessionId, activeRunId]);

    const handleRegenerate = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.REGENERATE_RESPONSE, { sessionId: currentSessionId });
        reload();
    }, [reload, currentSessionId]);

    useEffect(() => {
        if (isLoading && isMicListening) stopMic();
    }, [isLoading, isMicListening, stopMic]);

    useEffect(() => {
        const sessionChanged = prevSessionIdRef.current !== currentSessionId;
        prevSessionIdRef.current = currentSessionId;

        if (sessionChanged) {
            prevMessagesCountRef.current = messages.length;
            scrollMessagesToBottom();
            setIsPinnedToBottom(true);
            if (isMicListening) stopMic();
            markVoiceUnused();
            return;
        }

        const grew = messages.length > prevMessagesCountRef.current;
        prevMessagesCountRef.current = messages.length;

        if (grew && messages[messages.length - 1]?.role === MESSAGE_ROLES.USER) {
            setIsPinnedToBottom(true);
            scrollMessagesToBottom();
            return;
        }

        if (!isPinnedToBottomRef.current) return;
        if (pendingScrollFrameRef.current) return;
        pendingScrollFrameRef.current = requestAnimationFrame(() => {
            pendingScrollFrameRef.current = 0;
            scrollMessagesToBottom();
        });
    }, [messages, currentSessionId, isMicListening, stopMic, markVoiceUnused, scrollMessagesToBottom]);

    const submitWithMicOff = useCallback((e, options) => {
        const usedVoice = finishDictation();
        const content = (options?.content ?? input) ?? '';
        const messageLength = content.trim().length;
        if (messageLength) {
            trackChatEvent(CHAT_EVENTS.SEND_MESSAGE, {
                sessionId: currentSessionId,
                isNewSession: !currentSessionId,
                messageLength,
                usedVoice,
                is_top_nav: false,
            });
        }
        return handleSubmit(e, options);
    }, [handleSubmit, finishDictation, input, currentSessionId]);

    const onInputChange = useCallback(e => onManualChange(e.target.value), [onManualChange]);

    const lastAssistantMessage = useMemo(
        () => messages.findLast(msg => msg.role === MESSAGE_ROLES.ASSISTANT) || null,
        [messages]
    );

    const agentActivity = useMemo(() => {
        if (!lastAssistantMessage) return null;
        const { steps = [], startedAt, finishedAt } = lastAssistantMessage;
        const finishedSteps = steps.filter(
            s => s.status === STEP_STATUS.COMPLETED || s.status === STEP_STATUS.FAILED
        );
        const runningStep = steps.find(s => s.status === STEP_STATUS.RUNNING);

        if (!isLoading) {
            if (finishedSteps.length === 0) return null;
            const totalMs = startedAt && finishedAt ? finishedAt - startedAt : 0;
            return {
                state: AGENT_STATE.DONE,
                currentTool: null,
                history: finishedSteps,
                totalDurationMs: totalMs,
            };
        }

        if (finishedAt) {
            return {
                state: AGENT_STATE.THINKING,
                currentTool: null,
                history: [],
                totalDurationMs: 0,
            };
        }

        if (runningStep) {
            return {
                state: AGENT_STATE.RUNNING,
                currentTool: { name: runningStep.title || 'Working' },
                history: finishedSteps,
                totalDurationMs: 0,
            };
        }

        return {
            state: AGENT_STATE.THINKING,
            currentTool: null,
            history: finishedSteps,
            totalDurationMs: 0,
        };
    }, [lastAssistantMessage, isLoading]);

    const lastMessageDashboardArtifacts = useMemo(() => {
        const lastMessage = messages?.[messages.length - 1];
        if (!lastMessage || lastMessage.role !== MESSAGE_ROLES.ASSISTANT) return null;
        return lastMessage.content?.filter(block => block.is_final_analysis);
    }, [messages]);

    const onDashboardClick = useCallback(artifacts => setDashboardArtifacts(artifacts), []);

    const scrollToBottom = useCallback(() => {
        scrollMessagesToBottom();
        setIsPinnedToBottom(true);
    }, [scrollMessagesToBottom]);

    const previewArtifacts = dashboardArtifacts?.length > 0 ? dashboardArtifacts : lastMessageDashboardArtifacts;

    return (
        <div className={css.conversation}>
            {feedbackToast && (
                <Toast
                    icon={feedbackToast === FEEDBACK_TOAST_VARIANT.SUCCESS ? 'check' : 'alert-triangle'}
                    onClose={() => setFeedbackToast(null)}
                >
                    <Translate
                        id={`${TRANSLATION_PREFIX}.FEEDBACK.${
                            feedbackToast === FEEDBACK_TOAST_VARIANT.SUCCESS ? 'SUBMITTED_TOAST' : 'FAILED_TOAST'
                        }`}
                    />
                </Toast>
            )}
            <div className={css.content}>
                <div className={css.messagesContainer}>
                    {showEmptyState && !hasMessages && (
                        <ChatEmptyState
                            onSuggestionClick={query => {
                                markVoiceUnused();
                                submitWithMicOff(null, { content: query });
                            }}
                            input={input}
                            handleInputChange={onInputChange}
                            handleSubmit={submitWithMicOff}
                            isLoading={isInputDisabled}
                            stop={handleStop}
                            onMicClick={onMicClick}
                            isMicListening={isMicListening}
                            micSupported={isMicSupported}
                        />
                    )}
                    {hasMessages && (
                        <div ref={messagesListRef} className={css.messagesList} onScroll={handleScroll}>
                            {messages.map(message => (
                                <ChatMessage
                                    key={message.id}
                                    message={message}
                                    sessionId={currentSessionId}
                                    sessionType={sessionType}
                                    isAdminMode={isAdminMode}
                                    onDashboardClick={onDashboardClick}
                                    onFeedbackSubmitted={handleFeedbackSubmitted}
                                    onFeedbackFailed={handleFeedbackFailed}
                                />
                            ))}
                        </div>
                    )}
                    {hasMessages && !isPinnedToBottom && !error && !stopError && (
                        <button
                            type="button"
                            className={css.jumpToBottom}
                            onClick={scrollToBottom}
                            aria-label="Scroll to latest message"
                        >
                            <Icon name="chevron-down" size={IconSizes.SMALL} interactive={false} />
                        </button>
                    )}
                    {(error || stopError) && (
                        <div className={css.chatError}>
                            <Icon
                                name="alert-triangle"
                                variant={IconVariants.ERROR}
                                size={IconSizes.SMALL}
                                className={css.chatErrorIcon}
                                interactive={false}
                            />
                            <span className={css.chatErrorMessage}>{error || stopError}</span>
                            {error && (
                                <button onClick={handleRegenerate} className={css.chatErrorRetry}>
                                    <Translate id={`${TRANSLATION_PREFIX}.RETRY`} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {isAdminMode && showSessionDebug && (
                    <SessionDebugPanel
                        sessionId={currentSessionId}
                        refreshKey={completedRunCount}
                        onClose={onSessionDebugClose}
                    />
                )}
                {hasMessages && (
                    <div className={css.bottomDock}>
                        {agentActivity && (
                            <AgentActivity
                                state={agentActivity.state}
                                currentTool={agentActivity.currentTool}
                                history={agentActivity.history}
                                totalDurationMs={agentActivity.totalDurationMs}
                            />
                        )}
                        <ChatInput
                            input={input}
                            handleInputChange={onInputChange}
                            handleSubmit={submitWithMicOff}
                            isLoading={isInputDisabled}
                            stop={handleStop}
                            onMicClick={onMicClick}
                            isMicListening={isMicListening}
                            micSupported={isMicSupported}
                        />
                    </div>
                )}
            </div>
            {previewArtifacts?.length > 0 && <PreviewPanel key={currentSessionId} artifacts={previewArtifacts} />}
        </div>
    );
};

ChatConversation.propTypes = {
    chat: PropTypes.shape({
        sessionType: PropTypes.string,
        messages: PropTypes.arrayOf(PropTypes.object).isRequired,
        input: PropTypes.string.isRequired,
        setInput: PropTypes.func.isRequired,
        handleInputChange: PropTypes.func.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        isLoading: PropTypes.bool.isRequired,
        isCancelling: PropTypes.bool.isRequired,
        activeRunId: PropTypes.string,
        error: PropTypes.string,
        stopError: PropTypes.string,
        reload: PropTypes.func.isRequired,
        stop: PropTypes.func.isRequired,
        currentSessionId: PropTypes.string,
    }).isRequired,
    showEmptyState: PropTypes.bool,
    showSessionDebug: PropTypes.bool,
    onSessionDebugClose: PropTypes.func,
};

export default ChatConversation;
